import type { RxJsonSchema } from 'rxdb'

/**
 * PullRequest Document Type (Denormalized for RxDB).
 *
 * A source-control pull request linked to a task. Provider-neutral (GitHub first). These rows
 * are a durable per-task history populated server-side only (Super Agent relay + GitHub
 * webhook) — the client treats them as READ-ONLY (the replication omits `mapToServer`).
 * A task can have multiple PRs over its life; merged/closed PRs are kept as history.
 */
export interface PullRequestDocument {
  id: string
  envId: string
  taskId: string

  provider: string
  repositoryFullName: string
  number: number
  providerPullRequestId: string | null
  url: string | null
  htmlUrl: string | null
  sourceBranch: string | null
  targetBranch: string | null
  headRepoFullName: string | null
  headSha: string | null

  // draft | open | merged | closed
  state: string
  // unknown | pending | passing | failing
  ciStatus: string
  // none | approved | changes_requested | review_required
  reviewStatus: string
  // unknown | mergeable | conflicting | blocked | unstable
  mergeability: string

  mergedOn: number | null
  providerUpdatedAt: number
  lastObservedSource: string | null
  authorGitHubLogin: string | null

  isDeleted: boolean

  // Audit fields
  createdOn: number
  createdBy: string
  updatedOn: number
  updatedBy: string
  deletedOn: number | null
  deletedBy: string | null

  // RxDB replication metadata
  updatedAt?: number
}

/**
 * RxDB Schema for PullRequest (collection key `workitempullrequests`).
 */
export const pullRequestSchema: RxJsonSchema<PullRequestDocument> = {
  version: 1,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: { type: 'string', maxLength: 36 },
    envId: { type: 'string', maxLength: 36 },
    taskId: { type: 'string', maxLength: 36 },

    provider: { type: 'string', maxLength: 30 },
    repositoryFullName: { type: 'string', maxLength: 512 },
    number: { type: 'number', minimum: 0, maximum: 9999999999, multipleOf: 1 },
    providerPullRequestId: { type: ['string', 'null'], maxLength: 128 },
    url: { type: ['string', 'null'], maxLength: 1024 },
    htmlUrl: { type: ['string', 'null'], maxLength: 1024 },
    sourceBranch: { type: ['string', 'null'], maxLength: 512 },
    targetBranch: { type: ['string', 'null'], maxLength: 512 },
    headRepoFullName: { type: ['string', 'null'], maxLength: 512 },
    headSha: { type: ['string', 'null'], maxLength: 64 },

    state: { type: 'string', maxLength: 20 },
    ciStatus: { type: 'string', maxLength: 20 },
    reviewStatus: { type: 'string', maxLength: 30 },
    mergeability: { type: 'string', maxLength: 20 },

    mergedOn: { type: ['number', 'null'], minimum: 0, maximum: 9999999999999, multipleOf: 1 },
    providerUpdatedAt: { type: 'number', minimum: 0, maximum: 9999999999999, multipleOf: 1 },
    lastObservedSource: { type: ['string', 'null'], maxLength: 20 },
    authorGitHubLogin: { type: ['string', 'null'], maxLength: 256 },

    isDeleted: { type: 'boolean', default: false },

    // Audit fields
    createdOn: { type: 'number', minimum: 0, maximum: 9999999999999, multipleOf: 1 },
    createdBy: { type: 'string', maxLength: 36 },
    updatedOn: { type: 'number', minimum: 0, maximum: 9999999999999, multipleOf: 1 },
    updatedBy: { type: 'string', maxLength: 36 },
    deletedOn: { type: ['number', 'null'], minimum: 0, maximum: 9999999999999, multipleOf: 1 },
    deletedBy: { type: ['string', 'null'], maxLength: 36 },

    // RxDB replication metadata
    updatedAt: { type: 'number', minimum: 0, maximum: 9999999999999, multipleOf: 1 },
  },
  required: [
    'id',
    'envId',
    'taskId',
    'provider',
    'repositoryFullName',
    'number',
    'state',
    'ciStatus',
    'reviewStatus',
    'mergeability',
    'providerUpdatedAt',
    'isDeleted',
    'createdOn',
    'createdBy',
    'updatedOn',
    'updatedBy',
  ],
  // Nullable fields are never indexed (RxDB limitation). Compound indexes start with envId.
  indexes: ['envId', ['envId', 'taskId'], 'updatedOn', ['envId', 'taskId', 'isDeleted']],
}

export const pullRequestMigrationStrategies = {
  // v0 -> v1: added authorGitHubLogin (PR author attribution). Backfilled null; server re-pulls fill it.
  1: (oldDoc: PullRequestDocument): PullRequestDocument => ({ ...oldDoc, authorGitHubLogin: null }),
}
